import { useState } from "react";
import { makeEconomy, type CanonicalStore, type Car, type Trip } from "../../core/index.ts";
import { TripForm } from "../forms/TripForm.tsx";
import { BackRow } from "../forms/Modal.tsx";
import { longDate, round0 } from "../format.ts";
import { TripRouteMap, tripRoutePoints } from "../components/TripRouteMap.tsx";

/** "1h 24m" / "42m" — the same shape the trip detail panel uses. */
function durationText(seconds: number): string {
  return seconds >= 3600
    ? `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
    : `${Math.max(1, Math.round(seconds / 60))}m`;
}

/** Purpose chip tint, matching the mobile rows. */
function purposeColor(purpose: string): string {
  const p = purpose.toLowerCase();
  if (p === "business") return "var(--cyan)";
  if (p === "medical") return "var(--redline)";
  return "var(--muted)";
}

export function TripsView({ store, car, save, onBack }: { store: CanonicalStore; car: Car; save: (s: CanonicalStore) => void; onBack: () => void }) {
  const metric = store.profile.unitSystem === "metric";
  const economy = makeEconomy(metric);
  const currency = store.profile.displayCurrency;
  const trips = store.trips.filter((t) => t.carId === car.id).sort((a, b) => (b.date ?? 0) - (a.date ?? 0));
  const speedFactor = metric ? 1 : 0.621371;
  const speedLabel = metric ? "km/h" : "mph";
  const [form, setForm] = useState<{ editing?: Trip } | null>(null);
  const [detail, setDetail] = useState<Trip | null>(null);

  // Keep the open detail view in step with edits/deletes made from inside it.
  const openTrip = detail ? (store.trips.find((t) => t.id === detail.id) ?? null) : null;

  if (openTrip) {
    return (
      <>
        <TripDetail
          trip={openTrip}
          currency={currency}
          economy={economy}
          metric={metric}
          onEdit={() => setForm({ editing: openTrip })}
          onBack={() => setDetail(null)}
        />
        {form && <TripForm store={store} car={car} editing={form.editing} onSave={save} onClose={() => setForm(null)} />}
      </>
    );
  }

  return (
    <div className="container glow">
      <BackRow onClick={onBack} />
      <div className="topbar" style={{ paddingTop: 0 }}>
        <div className="title">TRIPS · <span className="car">{car.model}</span></div>
        <button className="btn ghost" style={{ padding: "8px 14px", fontSize: 11 }} onClick={() => setForm({})}>+ ADD</button>
      </div>
      <div className="eyebrow amber">ROUTE · LOGBOOK</div>
      <h1 className="headline">THE <span className="accent">trips.</span></h1>

      {trips.length === 0 && <div className="panel mono muted" style={{ fontSize: 12 }}>No trips logged. Tap “+ ADD”.</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }}>
        {trips.map((t) => {
          const distKm = Math.max(0, t.endOdometer - t.startOdometer);
          const cost = (t.fuelCost ?? 0) + (t.tollCost ?? 0) + (t.parkingCost ?? 0);
          const route = [t.startLocationName, ...(t.waypoints ?? []), t.endLocationName].filter(Boolean).join(" → ");
          const logCount = (t.fuelLogIds ?? []).length;
          // The telemetry strip the mobile rows carry: duration, speeds, and how
          // much fuel or charge the trip used. Speeds are km/h on the wire and in
          // storage — converted only here, for display.
          const stats: { k: string; v: string }[] = [];
          if (t.durationSec != null && t.durationSec > 0) {
            stats.push({ k: "TIME", v: durationText(t.durationSec) });
          }
          if (t.avgSpeedKmh != null && t.avgSpeedKmh > 0) {
            stats.push({ k: "AVG", v: `${round0(t.avgSpeedKmh * speedFactor)} ${speedLabel}` });
          }
          if (t.topSpeedKmh != null && t.topSpeedKmh > 0) {
            stats.push({ k: "TOP", v: `${round0(t.topSpeedKmh * speedFactor)} ${speedLabel}` });
          }
          if (t.fuelVolume != null && t.fuelVolume > 0) {
            stats.push({ k: "USED", v: `${economy.convertVolume(t.fuelVolume).toFixed(1)} ${economy.volumeLabel()}` });
          } else if (t.fuelEnergy != null && t.fuelEnergy > 0) {
            stats.push({ k: "USED", v: `${t.fuelEnergy.toFixed(1)} kWh` });
          }
          return (
            <div key={t.id} className="panel" style={{ cursor: "pointer" }} onClick={() => setDetail(t)}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <span className="mono muted" style={{ fontSize: 11 }}>{longDate(t.date ?? null)}</span>
                <span className="mono" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span className="cyan">{round0(economy.convertDistance(distKm))} {economy.distanceLabel()}</span>
                  {cost > 0 && <span className="amber">{round0(cost)} {currency}</span>}
                  {logCount > 0 && (
                    <span style={{
                      display: "inline-flex", alignItems: "baseline", gap: 3,
                      background: "color-mix(in srgb, var(--amber) 12%, transparent)",
                      border: "1px solid color-mix(in srgb, var(--amber) 35%, transparent)",
                      borderRadius: 4, padding: "2px 6px",
                    }}>
                      <span className="amber" style={{ fontSize: 9, fontWeight: 700 }}>{logCount}</span>
                      <span style={{ fontSize: 7, color: "color-mix(in srgb, var(--amber) 70%, transparent)" }}>LOGS</span>
                    </span>
                  )}
                </span>
              </div>
              {(() => {
                const pts = tripRoutePoints(t);
                return pts.length >= 2 ? (
                  <div style={{ marginTop: 8 }}>
                    <TripRouteMap points={pts} height={120} />
                  </div>
                ) : null;
              })()}
              {route && <div className="mono cream" style={{ fontSize: 13, marginTop: 6 }}>{route}</div>}
              {stats.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 6 }}>
                  {stats.map((st) => (
                    <span key={st.k} style={{ display: "inline-flex", alignItems: "baseline", gap: 5 }}>
                      <span className="mono muted" style={{ fontSize: 8, letterSpacing: 1.4 }}>{st.k}</span>
                      <span className="mono" style={{ fontSize: 10, fontWeight: 700, color: "var(--creamDim)" }}>{st.v}</span>
                    </span>
                  ))}
                </div>
              )}
              {t.purpose && (
                <div style={{ marginTop: 6 }}>
                  <span className="mono" style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: 1.4,
                    color: purposeColor(t.purpose),
                    background: `color-mix(in srgb, ${purposeColor(t.purpose)} 12%, transparent)`,
                    border: `1px solid color-mix(in srgb, ${purposeColor(t.purpose)} 50%, transparent)`,
                    borderRadius: 6, padding: "3px 8px",
                  }}>
                    {t.purpose.toUpperCase()}
                  </span>
                </div>
              )}
              {t.notes && (
                <div className="mono" style={{
                  fontSize: 11, marginTop: 6, color: "var(--creamDim)",
                  display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                }}>
                  {t.notes}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {form && <TripForm store={store} car={car} editing={form.editing} onSave={save} onClose={() => setForm(null)} />}
    </div>
  );
}

/**
 * Full detail for one trip: the recorded route on a map, every named stop, the
 * cost breakdown and the telemetry the recording captured. This is where the
 * fields the list can't show — waypoints, coordinates, duration, speeds,
 * linked fuel logs — actually surface, so nothing that survives an import is
 * invisible on the web.
 */
function TripDetail({
  trip, currency, economy, metric, onEdit, onBack,
}: {
  trip: Trip;
  currency: string;
  economy: ReturnType<typeof makeEconomy>;
  metric: boolean;
  onEdit: () => void;
  onBack: () => void;
}) {
  const points = tripRoutePoints(trip);
  const distKm = Math.max(0, trip.endOdometer - trip.startOdometer);
  const total = (trip.fuelCost ?? 0) + (trip.tollCost ?? 0) + (trip.parkingCost ?? 0);
  const stops = [trip.startLocationName, ...(trip.waypoints ?? []), trip.endLocationName].filter(Boolean) as string[];

  // Speeds are km/h on the wire and in storage — converted only for display.
  const speedFactor = metric ? 1 : 0.621371;
  const speedLabel = metric ? "km/h" : "mph";
  const duration = trip.durationSec ?? null;
  const durationLabel = duration == null ? null : durationText(duration);

  const row = (label: string, value: string) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
      <span className="mono muted" style={{ fontSize: 10, letterSpacing: 1.6 }}>{label}</span>
      <span className="mono cream" style={{ fontSize: 12 }}>{value}</span>
    </div>
  );

  return (
    <div className="container glow">
      <BackRow onClick={onBack} />
      <div className="topbar" style={{ paddingTop: 0 }}>
        <div className="title">TRIP</div>
        <button className="btn ghost" style={{ padding: "8px 14px", fontSize: 11 }} onClick={onEdit}>EDIT</button>
      </div>
      <div className="eyebrow amber">ROUTE · DETAIL</div>
      <h1 className="headline">THE <span className="accent">trip.</span></h1>

      {points.length >= 2 && (
        <div style={{ marginTop: 10 }}>
          <TripRouteMap points={points} height={260} />
        </div>
      )}

      {stops.length > 0 && (
        <div className="panel" style={{ marginTop: 10 }}>
          <div className="mono muted" style={{ fontSize: 10, letterSpacing: 1.6, marginBottom: 6 }}>ROUTE</div>
          {stops.map((stop, i) => (
            <div key={`${stop}-${i}`} className="mono cream" style={{ fontSize: 13, padding: "3px 0" }}>
              <span className="amber" style={{ marginRight: 8 }}>
                {i === 0 ? "A" : i === stops.length - 1 ? "B" : "·"}
              </span>
              {stop}
            </div>
          ))}
        </div>
      )}

      <div className="panel" style={{ marginTop: 10 }}>
        {row("DATE", longDate(trip.date ?? null))}
        {row("DISTANCE", `${round0(economy.convertDistance(distKm))} ${economy.distanceLabel()}`)}
        {durationLabel && row("DURATION", durationLabel)}
        {trip.avgSpeedKmh != null && row("AVG SPEED", `${round0(trip.avgSpeedKmh * speedFactor)} ${speedLabel}`)}
        {trip.topSpeedKmh != null && row("TOP SPEED", `${round0(trip.topSpeedKmh * speedFactor)} ${speedLabel}`)}
        {trip.avgConsumptionOverride != null && row("AVG CONSUMPTION", String(trip.avgConsumptionOverride))}
        {trip.purpose && row("PURPOSE", trip.purpose.toUpperCase())}
        {trip.passengers != null && row("PASSENGERS", String(trip.passengers))}
      </div>

      {total > 0 && (
        <div className="panel" style={{ marginTop: 10 }}>
          {trip.fuelCost != null && row("FUEL", `${round0(trip.fuelCost)} ${currency}`)}
          {trip.tollCost != null && row("TOLLS", `${round0(trip.tollCost)} ${currency}`)}
          {trip.parkingCost != null && row("PARKING", `${round0(trip.parkingCost)} ${currency}`)}
          {row("TOTAL", `${round0(total)} ${currency}`)}
          {trip.mileageRate != null && row("MILEAGE RATE", `${trip.mileageRate} ${currency}/${economy.distanceLabel()}`)}
        </div>
      )}

      {(trip.fuelLogIds ?? []).length > 0 && (
        <div className="panel mono muted" style={{ marginTop: 10, fontSize: 11 }}>
          {(trip.fuelLogIds ?? []).length} linked fuel/charge log(s)
        </div>
      )}

      {trip.notes && (
        <div className="panel mono cream" style={{ marginTop: 10, fontSize: 12 }}>{trip.notes}</div>
      )}
    </div>
  );
}
